import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AdminNav } from "@/components/admin/AdminNav";
import { errMsg, useApi } from "@/lib/api";
import { useMe } from "@/lib/me";

const KAT = { guna: "Cara guna ATAS", metodologi: "Metodologi HTE" };
const EMPTY = { kategori: "guna", teks: "", aktif: true };

const TipForm = ({ init, onSave, onCancel, busy }) => {
  const [f, setF] = useState(init);
  return (
    <div className="surface rounded-2xl border border-line p-4 space-y-3" data-testid="tip-form">
      <div className="flex flex-wrap gap-3 items-center">
        <select value={f.kategori} onChange={(e) => setF({ ...f, kategori: e.target.value })} data-testid="tip-form-kategori" className="h-10 px-3 rounded-xl bg-bg border border-line text-sm">
          {Object.entries(KAT).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <label className="text-sm inline-flex items-center gap-2"><input type="checkbox" checked={f.aktif} onChange={(e) => setF({ ...f, aktif: e.target.checked })} data-testid="tip-form-aktif" /> Aktif</label>
      </div>
      <textarea rows={3} value={f.teks} onChange={(e) => setF({ ...f, teks: e.target.value })} placeholder="Teks tip…" data-testid="tip-form-teks"
        className="w-full px-3 py-2 rounded-xl bg-bg border border-line text-sm focus:outline-none focus:border-[var(--q)]" />
      <div className="flex gap-2">
        <button onClick={() => onSave(f)} disabled={busy || f.teks.trim().length < 5} data-testid="tip-form-save" className="h-10 px-5 rounded-full bg-ink text-bg text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40">{busy && <Loader2 size={14} className="animate-spin" />} Simpan</button>
        <button onClick={onCancel} className="h-10 px-4 rounded-full border border-line text-sm" data-testid="tip-form-cancel">Batal</button>
      </div>
    </div>
  );
};

export default function AdminTipsPage() {
  const api = useApi();
  const me = useMe();
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api("get", "/admin/tips").then(setItems).catch(() => setItems([])), [api]);
  useEffect(() => { if (me?.role === "superadmin") load(); }, [me, load]);
  if (me && me.role !== "superadmin") return <Navigate to="/" replace />;
  const save = async (f) => {
    setBusy(true);
    try { edit === "new" ? await api("post", "/admin/tips", f) : await api("put", `/admin/tips/${edit.id}`, f); toast.success("Tip disimpan"); setEdit(null); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (t) => {
    if (!window.confirm("Padam tip ini?")) return;
    try { await api("delete", `/admin/tips/${t.id}`); toast.success("Tip dipadam"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-10 space-y-6" data-testid="admin-tips-page">
      <AdminNav />
      <div className="flex items-end justify-between gap-3">
        <h1 className="font-display text-4xl text-ink">Tips</h1>
        {!edit && <button onClick={() => setEdit("new")} data-testid="tip-add-button" className="h-10 px-5 rounded-full bg-ink text-bg text-sm font-semibold inline-flex items-center gap-2"><Plus size={14} /> Tambah tip</button>}
      </div>
      <p className="text-sm text-mute">Tips aktif dipaparkan secara rawak di halaman utama dan Ruang Kerja Saya, dan bertukar setiap 20 saat atau apabila halaman dimuat semula.</p>
      {edit === "new" && <TipForm init={EMPTY} onSave={save} onCancel={() => setEdit(null)} busy={busy} />}
      {!items ? <Loader2 className="animate-spin text-q" /> : (
        <ul className="space-y-2" data-testid="tip-list">
          {items.map((t) => edit?.id === t.id ? <li key={t.id}><TipForm init={t} onSave={save} onCancel={() => setEdit(null)} busy={busy} /></li> : (
            <li key={t.id} className="rounded-xl border border-line p-4 flex gap-3 items-start" data-testid={`tip-row-${t.id}`}>
              <div className={`flex-1 text-sm ${t.aktif ? "" : "opacity-50"}`}><span className="eyebrow">{KAT[t.kategori]}{!t.aktif && " · tidak aktif"}</span><p className="text-ink mt-1">{t.teks}</p></div>
              <button onClick={() => setEdit(t)} aria-label="Sunting tip" data-testid={`tip-edit-${t.id}`} className="h-8 w-8 grid place-items-center rounded-full hover:bg-surface"><Pencil size={14} /></button>
              <button onClick={() => remove(t)} aria-label="Padam tip" data-testid={`tip-delete-${t.id}`} className="h-8 w-8 grid place-items-center rounded-full hover:bg-warn-soft hover:text-warn"><Trash2 size={14} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
